import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  ViewEncapsulation,
  viewChildren,
  viewChild,
  computed,
  ChangeDetectionStrategy,
  signal,
  inject,
  effect,
  untracked,
  linkedSignal,
  resource,
} from '@angular/core';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ClarityModule, ClrForm, ClrLoadingState } from '@clr/angular';
import { ToastService } from 'src/app/services/toast.service';
import { Subject } from 'rxjs';
import { AutofocusDirective } from 'src/app/directives/autofocus.directive';
import { WindowMediaQueryDirective } from 'src/app/directives/window-media-query.directive';
import { Column } from 'src/app/models/ui/table';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { CustomValidators } from 'src/app/shared/classes/custom-validators';
import { FormGroupOfType } from 'src/app/types/shared';
import { SOTYP_Map } from 'src/app/models/api/SOTYP';
import { HilfeBeschreibungModalComponent } from '../../_modals/hilfebeschreibungmodal/hilfebeschreibungmodal.component';
import { PreviewThumbnailsComponent } from '../../_shared/preview-thumbnails/preview-thumbnails.component';
import { FilterTolByNameOptionsPipe } from './filter-tol-by-options.pipe';
import { TransformColNamesPipe } from './transform-col-names.pipe';
import { WerteRowValuesPipe } from './werte-row-values.pipe';
import { ZaehlerEinheitenComponent } from './zaehler-einheiten/zaehler-einheiten.component';
import {
  TrackMode,
  TrackModeLongNames,
  ZArt,
  ZArtLongNames,
  ZArtOptions,
  ZType,
  ZTypeLongNames,
  ZählerMode,
} from './zaehler.enums';
import { Genauigkeit, TOL_MINUS, ZählerName, ZählerWert, TOL_AKTIV, TOL_PLUS, ZählerWertModel } from './zaehler.types';
import { AtIndexArrayPipe } from 'src/app/pipes/array-methods.pipe';
import { ZaehlerImportComponent } from './zaehler-import/zaehler-import.component';
import { ID, MRproResponseInfo } from 'src/app/types/api';
import { ZaehlerApiService } from 'src/app/services/APIService/zaehler.api.service';
import {
  CdkDrag,
  CdkDragHandle,
  CdkDragPreview,
  CdkDropList,
  moveItemInArray,
  type CdkDragDrop,
} from '@angular/cdk/drag-drop';
import { ConfirmService } from 'src/app/services/confirm.service';
import { ArrayMethodsCallbackPipe } from 'src/app/pipes/array-methods-callback.pipe';
import { HttpErrorResponse } from '@angular/common/http';
import { FileForPreview } from '../../_shared/preview-thumbnails/preview-thumbnail.type';
import { EditSkizzenPoints } from './edit-skizzen-points/edit-skizzen-points';
import { errorHandle } from 'src/app/misc/functions';

@Component({
  selector: 'app-zaehlerverwaltung',
  templateUrl: './zaehlerverwaltung.component.html',
  styleUrls: ['./zaehlerverwaltung.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    HilfeBeschreibungModalComponent,
    PreviewThumbnailsComponent,
    NgxSliderModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    CdkDragPreview,
    FilterTolByNameOptionsPipe,
    TransformColNamesPipe,
    WerteRowValuesPipe,
    AutofocusDirective,
    WindowMediaQueryDirective,
    ZaehlerEinheitenComponent,
    ZaehlerImportComponent,
    MrTranslatePipe,
    FormsModule,
    ReactiveFormsModule,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
    AtIndexArrayPipe,
    ArrayMethodsCallbackPipe,
    EditSkizzenPoints,
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZaehlerverwaltungComponent {
  private readonly api = inject(ZaehlerApiService);
  private readonly sharedAPI = inject(APIService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastService);
  private readonly confirmService = inject(ConfirmService);

  public readonly closedialog = new Subject<boolean>();

  private readonly previews = viewChildren(PreviewThumbnailsComponent);
  private readonly preview_aktiv = computed(() => this.previews().find(preview => !preview.disabled()));
  private readonly preview_readonly = computed(() => this.previews().find(preview => preview.disabled()));

  private readonly zNamesContainer = viewChild('zNamesContainer', { read: ElementRef });
  private readonly zWerteContainer = viewChild('zWerteContainer', { read: ElementRef });

  private readonly trackClrForm = viewChild('trackForm', { read: ClrForm });
  private readonly wertClrForm = viewChild('wertForm', { read: ClrForm });

  protected readonly mode = signal(ZählerMode.ZÄHLER);

  protected readonly objekte = toSignal<SOTYP_Map>(this.sharedAPI.getSOTYP());
  protected readonly tol = this.api.getToleranzenBezeichnungen();

  protected readonly otypid = signal(1);
  protected readonly skizzenListe = this.sharedAPI.getSkizzenNames().value;

  protected readonly zaehlerNamesRs = this.api.getZaehlerNames(this.otypid, this.mode);
  private readonly zNamesCodes = computed(() => this.zaehlerNamesRs.value().rows.map(row => row.Code));

  protected readonly isMultiModeNames = signal(false);
  protected readonly selectedNames = linkedSignal<ZählerName[]>(() => (this.otypid(), this.isMultiModeNames(), []));
  protected readonly selectedName = computed(() => this.selectedNames()[0]);
  protected readonly selectedNameID = computed(() => this.selectedName()?.ID);
  protected readonly skizzenName = computed(() => this.selectedName()?.Bild || null);
  protected readonly selectedSkizze = computed(() => this.preview_readonly()?.data()[0]?.base64);

  protected readonly zaehlerWerteRs = this.api.getZaehlerWerteBySZAEHNID(this.selectedNameID);
  protected readonly werte = computed(() => this.zaehlerWerteRs.value().rows);
  protected readonly zWerteCodes = computed(() => this.werte().map(row => row.Code));
  private readonly maxSort = computed(() => Math.max(0, ...this.werte().map(row => row.I_SORT)));

  protected readonly isMultiModeWerte = signal(false);
  protected readonly selectedWerte = linkedSignal<ZählerWert[]>(
    () => (this.selectedNameID(), this.isMultiModeWerte(), [])
  );
  protected readonly selectedWert = computed(() => this.selectedWerte()[0]);
  protected readonly wertIndex = signal<number>(undefined);

  protected readonly copiedWerte = signal<ZählerWert[]>([]);

  protected readonly mehRs = this.api.getZaehlerWerteEinheiten(this.otypid);
  protected readonly meh = computed(() => this.mehRs.value().rows);
  private readonly defaultEinheit = computed(() => this.meh()?.[0]?.ID);

  protected readonly isMessGeraetOptionOn = this.sharedAPI.getTypOption(
    () => (this.mode() == ZählerMode.ZÄHLER ? this.otypid() : undefined),
    'Opt_F_ZaehlerMessreg'
  );
  protected readonly filterTrackColumns = ({ id }: Column) => {
    if (id.startsWith('Track')) return this.isMessGeraetOptionOn();
    if (id == 'ZArt') return !this.isMessGeraetOptionOn();
    return true;
  };

  protected readonly availableTracks = computed(() => getTracks(this.selectedName()));

  protected readonly isOpenModalZaehlerName = signal(false);
  protected readonly isOpenModalZaehlerWert = signal(false);
  protected readonly isOpenModalZaehlerEinheit = signal(false);
  protected readonly isOpenModalZaehlerImport = signal(false);
  protected readonly isOpenModalSkizzen = signal(false);
  protected readonly isOpenModalHelper = signal(false);

  protected readonly trackResetState = signal(ClrLoadingState.DEFAULT);
  protected readonly wertState = signal(ClrLoadingState.DEFAULT);

  protected readonly LOADING_STATE = ClrLoadingState;
  protected readonly ZAEHLER_MODE = ZählerMode;

  protected readonly nameGroupForm = new FormGroupOfType<ZählerName>({
    ID: new FormControl<number>(null),
    Code: new FormControl<string>(null, { updateOn: 'blur' }), //* Dynamisch Required + InTableValidator
    Bezeichnung: new FormControl<string>(null),
    isSRG: new FormControl<boolean>(false, { nonNullable: true }),
    isSR100: new FormControl<boolean>(false, { nonNullable: true }),
    Bild: new FormControl<string>(null),
  });
  protected readonly skizzenNameInForm = toSignal(this.nameGroupForm.controls.Bild.valueChanges);

  protected readonly uniqueTracksValidator: ValidatorFn = (form: typeof this.trackFormArray) => {
    const tracks = form.value.filter(track => track !== null);
    if (new Set(tracks).size != tracks.length) return { 'track-used-already': true };
    return null;
  };

  protected readonly anlageTrackValidator: ValidatorFn = (form: typeof this.trackFormArray) => {
    if (form.length > 1 && form.value.includes(TrackMode.Anlage)) return { 'track-anlage-als-wert': true };
    return null;
  };

  protected readonly trackFormArray = new FormArray<FormControl<TrackMode>>(
    [],
    [this.uniqueTracksValidator, this.anlageTrackValidator]
  );

  protected readonly wertGroupForm = new FormGroupOfType<ZählerWertModel>({
    ID: new FormControl<number>(null),
    Code: new FormControl<string>(null, { updateOn: 'blur' }), //* Dynamisch Required + InTableValidator
    Bezeichnung: new FormControl<string>(null),
    Soll: new FormControl<number>(null),

    aktivSR100: new FormControl<boolean>(true, { nonNullable: true }),
    SR100plus: new FormControl<number>(null),
    SR100minus: new FormControl<number>(null),

    aktivSRlimm: new FormControl<boolean>(true, { nonNullable: true }),
    SRlimplus: new FormControl<number>(null),
    SRlimminus: new FormControl<number>(null),

    aktivSRG: new FormControl<boolean>(true, { nonNullable: true }),
    SRGplus: new FormControl<number>(null),
    SRGminus: new FormControl<number>(null),

    ZEinheit: new FormControl<number>(null),
    I_SORT: new FormControl<number>(null),

    SGenau: new FormControl<Genauigkeit>(0, { nonNullable: true }),
    TGenau: new FormControl<Genauigkeit>(0, { nonNullable: true }),

    ZArt: new FormControl<ZArt>(ZArt.Numerisch, { nonNullable: true }),
    ZType: new FormControl<ZType>(null),
    isHeader: new FormControl<boolean>(false),

    Track: new FormControl(0, { nonNullable: true }),
  });

  protected readonly ZType_Header = ZType.Header;
  protected readonly ZArt_Options = ZArtOptions;
  protected readonly ZArt_Map = ZArtLongNames;
  protected readonly ZType_Map = ZTypeLongNames;
  protected readonly Track_Map = TrackModeLongNames;

  public set data(dataIn: any) {
    this.otypid.set(dataIn.otypid ?? 1);
    this.mode.set(dataIn.mode ?? ZählerMode.ZÄHLER);
  }

  constructor() {
    this.scrollEffects();

    effect(() => {
      const name = this.skizzenName();
      untracked(() => {
        if (name && name == this.skizzenNameInForm()) {
          this.preview_readonly().copyFrom(this.preview_aktiv());
        } else {
          this.getSkizzeInPreview(this.preview_readonly(), name);
        }
      });
    });
    effect(() => {
      const name = this.skizzenNameInForm();
      untracked(() => {
        if (name && name == this.skizzenName()) {
          this.preview_aktiv().copyFrom(this.preview_readonly());
        } else {
          this.getSkizzeInPreview(this.preview_aktiv(), name);
        }
      });
    });

    effect(() => {
      if (this.isOpenModalZaehlerImport() && this.zaehlerNamesRs.status() == 'resolved')
        untracked(() => this.checkDuplicateCodes());
    });

    effect(() => {
      if (this.isMessGeraetOptionOn()) {
        const trackN = this.availableTracks().length;
        const { Track, ZType } = this.wertGroupForm.controls;
        Track.setValidators(trackN > 1 ? [Validators.required, Validators.max(trackN - 1)] : []);
        Track.updateValueAndValidity();
        ZType.setValidators(Validators.required);
        ZType.updateValueAndValidity();
      }
    });

    this.catchFormChanges();
  }

  private scrollEffects() {
    effect(() => {
      if (this.zNamesContainer() && this.zaehlerNamesRs.status() == 'resolved' && this.selectedName()?.ID)
        this.zNamesContainer()
          .nativeElement.querySelector('#i-' + this.selectedName().ID)
          ?.scrollIntoView({
            behavior: 'auto',
            block: 'nearest',
          });
    });
    effect(() => {
      if (this.zWerteContainer() && this.zaehlerWerteRs.status() == 'resolved' && this.selectedWert()?.ID)
        this.zWerteContainer()
          .nativeElement.querySelector('#i-' + this.selectedWert().ID)
          ?.scrollIntoView({
            behavior: 'auto',
            block: 'nearest',
          });
    });
  }

  private catchFormChanges() {
    this.wertGroupForm.controls.isHeader.valueChanges.pipe(takeUntilDestroyed()).subscribe(isHeader => {
      const artCtrl = this.wertGroupForm.controls.ZArt;
      if (!isHeader && !artCtrl.value) artCtrl.setValue(ZArt.Numerisch, { emitEvent: false });
      this.toggleRequiredControls(artCtrl.value, isHeader);
    });

    this.wertGroupForm.controls.ZType.valueChanges.pipe(takeUntilDestroyed()).subscribe(type => {
      if (!this.isMessGeraetOptionOn()) return;
      const artCtrl = this.wertGroupForm.controls.ZArt;
      if (type == ZType.Zustand) artCtrl.setValue(ZArt.JaNein, { emitEvent: false });
      else artCtrl.setValue(ZArt.Numerisch, { emitEvent: false });
      this.toggleRequiredControls(artCtrl.value);
    });

    this.wertGroupForm.controls.ZArt.valueChanges.pipe(takeUntilDestroyed()).subscribe(art => {
      this.toggleRequiredControls(art);
    });
  }

  protected addItem(mode: 'name' | 'wert') {
    switch (mode) {
      case 'name':
        this.selectedNames.set([]);
        break;
      case 'wert':
        this.selectedWerte.set([]);
        break;
    }
    this.editItem(mode);
  }

  protected addTrack() {
    if (this.trackFormArray.length == 4) return;
    this.trackClrForm().markAsTouched();
    this.trackFormArray.push(new FormControl<TrackMode>(null, Validators.required));
  }

  protected async deleteTrack() {
    const lastIndex = this.trackFormArray.length - 1;
    if (this.trackFormArray.length > 1 && (await this.canDeleteTrack(lastIndex)))
      this.trackFormArray.removeAt(lastIndex);
  }

  protected async canDeleteTrack(track: number) {
    const usedInWerten = this.werte().some(wert => wert.Track == track);
    if (!usedInWerten) return true;

    this.trackResetState.set(ClrLoadingState.LOADING);
    const text = this.mrTranslate.transform('Gelöschten Strang wird in Zählerwerten zurückgesetzt');
    if (!(await this.confirmService.danger(text))) return false;

    try {
      const { success, error } = await this.api.resetTrackInZaehlerWerten(this.selectedName().ID, track);
      if (!success) throw new Error(error);
      this.trackResetState.set(ClrLoadingState.SUCCESS);
      this.zaehlerWerteRs.reload();
      return true;
    } catch (er) {
      this.trackResetState.set(ClrLoadingState.DEFAULT);
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
      return false;
    }
  }

  protected resetTracks(track: [TrackMode, TrackMode, TrackMode, TrackMode]) {
    this.trackFormArray.clear();
    this.trackFormArray.push(new FormControl(track[0] ?? TrackMode.Anlage, Validators.required));
    for (let i = 1; i < 4; i++) {
      if (!TrackMode[track[i]]) continue;
      this.trackFormArray.push(new FormControl(track[i], Validators.required));
    }
  }

  protected async editItem(mode: 'name' | 'wert') {
    switch (mode) {
      case 'name':
        const name = this.selectedName();
        this.nameGroupForm.controls.Code.setValidators([
          Validators.required,
          CustomValidators.NotInTableValidator(this.zNamesCodes(), name?.Code),
        ]);
        this.nameGroupForm.reset(name);
        this.resetTracks([name?.Track0, name?.Track1, name?.Track2, name?.Track3]);

        this.isOpenModalZaehlerName.set(true);
        break;
      case 'wert':
        const wert = this.selectedWert();
        this.wertIndex.set(this.werte().indexOf(this.selectedWert()));
        this.wertGroupForm.controls.Code.setValidators([
          Validators.required,
          CustomValidators.NotInTableValidator(this.zWerteCodes(), wert?.Code),
        ]);
        this.wertGroupForm.reset({
          ...wert,
          ZEinheit: wert?.ZEinheit ?? this.defaultEinheit(),
          isHeader: wert?.ZType == ZType.Header,
          I_SORT: wert?.I_SORT ?? this.maxSort() + 1,
        });

        this.isOpenModalZaehlerWert.set(true);
        break;
    }
  }

  protected async saveItem(mode: 'name' | 'wert') {
    switch (mode) {
      case 'name': {
        this.trackClrForm()?.markAsTouched();
        if (this.nameGroupForm.invalid || this.trackFormArray.invalid) return;

        const changed = this.nameGroupForm.dirty;
        let value: ZählerName = this.nameGroupForm.getRawValue();

        const [Track0, Track1, Track2, Track3] = this.trackFormArray.value;
        value = { ...value, Track0: Track0, Track1: Track1, Track2: Track2, Track3: Track3 };
        try {
          const { success, error, ID } =
            !changed ? { success: true } : await this.api.setZaehlerName(this.otypid(), this.mode(), value);

          if (!success) throw new Error(error);
          this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert!'));
          this.isOpenModalZaehlerName.set(false);

          if (changed) {
            this.selectedNames.set([{ ID } as ZählerName]);
            this.zaehlerNamesRs.reload();
          }
        } catch (er) {
          this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
        }

        break;
      }
      case 'wert': {
        try {
          const { success, error, ID } = await this.saveWert(true);
          if (!success) throw new Error(error);

          this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert!'));
          this.isOpenModalZaehlerWert.set(false);

          this.selectedWerte.set([{ ID } as ZählerWert]);
          this.zaehlerWerteRs.reload();

          this.wertState.set(ClrLoadingState.DEFAULT);
        } catch (er) {
          this.wertClrForm().markAsTouched();
          this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
        }
        break;
      }
    }
  }

  protected async nextWert(direction: 1 | -1, event: PointerEvent) {
    try {
      const { success, error, pristine } = await this.saveWert(event.ctrlKey);
      if (!success) throw new Error(error);
      if (!pristine /* changed */) {
        setTimeout(() => this.wertState.set(ClrLoadingState.SUCCESS), 100);
        this.zaehlerWerteRs.reload();
      }

      const list = this.werte();
      this.wertIndex.update(ind => ind + direction);
      this.selectedWerte.set([list.at(this.wertIndex())]);
      this.editItem('wert');
    } catch (er) {
      this.wertState.set(ClrLoadingState.DEFAULT);
      this.wertClrForm().markAsTouched();
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }
  private async saveWert(forceSave = false): Promise<MRproResponseInfo<ID & { pristine: true }>> {
    let invVals = '';
    for (const name in this.wertGroupForm.controls) {
      if (this.wertGroupForm.controls[name].invalid) {
        invVals += name + ' ';
      }
    }
    if (this.wertGroupForm.invalid) return { success: false, error: 'Form is invalid: ' + invVals + ' required' };
    if (!forceSave && this.wertGroupForm.pristine) return { success: true, ID: this.selectedWert().ID, pristine: true };

    this.wertState.set(ClrLoadingState.LOADING);
    const value = this.clearFields(this.wertGroupForm.getRawValue());

    return this.api.setZaehlerWert(this.selectedName().ID, value);
  }

  private clearFields(value: ZählerWertModel): ZählerWert {
    let model: ZählerWert = {
      ID: value.ID,
      I_SORT: value.I_SORT,
      Code: value.Code,
      Bezeichnung: value.Bezeichnung,
      ZArt: ZArt.None,
      ZType: null,
      Soll: null,
      SGenau: 0,
      TGenau: 0,
      ZEinheit: 0,
      aktivSR100: null,
      SR100minus: null,
      SR100plus: null,
      aktivSRlimm: null,
      SRlimminus: null,
      SRlimplus: null,
      aktivSRG: null,
      SRGminus: null,
      SRGplus: null,
      Track: 0,
    };

    if (value.isHeader) {
      return { ...model, ZType: ZType.Header };
    } else {
      model.ZType = value.ZType ?? ZType.NotHeader;
      model.ZArt = +value.ZArt || ZArt.None;

      if (value.Track < this.availableTracks().length) model.Track = value.Track;

      if (model.ZArt == ZArt.JaNein) {
        Object.keys(TOL_AKTIV).forEach(key => (model[key] = value[key] ?? false));
        return model;
      }

      const mode = ZArtOptions.get(model.ZArt);
      const minus = mode.minus?.input;
      const plus = mode.plus?.input;

      if (minus == 'Num') Object.keys(TOL_MINUS).forEach(key => (model[key] = value[key] || null));

      if (plus == 'Num') Object.keys(TOL_PLUS).forEach(key => (model[key] = value[key] || null));

      if (minus || plus) {
        const name = this.selectedName();
        model.aktivSRlimm = value.aktivSRlimm ?? false;
        if (name.isSR100) model.aktivSR100 = value.aktivSR100 ?? false;
        if (name.isSRG) model.aktivSRG = value.aktivSRG ?? false;
      }

      model.Soll = mode.soll.input == 'Num' ? +value.Soll : null;
      model.ZEinheit = value.ZEinheit ?? 0;
      model.TGenau = value.TGenau ?? 0;
      model.SGenau = value.SGenau ?? 0;

      return model;
    }
  }

  private toggleRequiredControls(art: ZArt, isHeader?: boolean) {
    const sollCtrl = this.wertGroupForm.controls.Soll;
    if (!isHeader && ZArtOptions.get(+art)?.soll.input == 'Num') sollCtrl.addValidators(Validators.required);
    else sollCtrl.removeValidators(Validators.required);
    sollCtrl.updateValueAndValidity();

    if (!this.defaultEinheit()) {
      const mehCtrl = this.wertGroupForm.controls.ZEinheit;
      if (!isHeader && art != ZArt.JaNein) mehCtrl.addValidators(Validators.required);
      else mehCtrl.removeValidators(Validators.required);
      mehCtrl.updateValueAndValidity();
    }
  }

  protected async deleteItem(mode: 'name' | 'wert') {
    const IDs = mode == 'name' ? this.selectedNames().map(r => r.ID) : this.selectedWerte().map(r => r.ID);

    const text = this.mrTranslate.transform('Sind Sie sicher, dass Sie diese Zeilen löschen wollen?');
    const ok = await this.confirmService.danger(text);
    if (!ok) return;
    try {
      const result = await Promise.all(IDs.map(ID => this.api.deleteZahlerItem(mode, ID)));
      const success = result.every(res => res.success);
      if (!success) throw new Error();
      this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
      switch (mode) {
        case 'name':
          this.selectedNames.set([]);
          // this.selectedSkizze.set(false);
          this.zaehlerNamesRs.reload();
          break;
        case 'wert':
          this.selectedWerte.set([]);
          this.zaehlerWerteRs.reload();
          break;
      }
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async copyItem(mode: 'name' | 'wert') {
    const CopyText = this.mrTranslate.transform('Kopie');
    switch (mode) {
      case 'name': {
        try {
          const ID = await this.api.copyZaehlerName(this.selectedName().ID, { CopyText });
          this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert!'));
          this.selectedNames.set([{ ID } as ZählerName]);
          this.zaehlerNamesRs.reload();
        } catch (er) {
          this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
        }
        break;
      }
      case 'wert': {
        try {
          const IDs = this.copiedWerte().map(wert => wert.ID);

          const { success, error } = await this.api.copyZaehlerWert(this.selectedName().ID, { IDs, CopyText });
          if (!success) throw new Error(error);

          this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert!'));
          this.selectedWerte.set([]);
          this.copiedWerte.set([]);
          this.zaehlerWerteRs.reload();
        } catch (er) {
          this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
        }
        break;
      }
    }
  }

  private async getSkizzeInPreview(preview: PreviewThumbnailsComponent, name: string) {
    if (!preview) return;
    let skizze: FileForPreview;
    if (name) {
      try {
        const base64 = await this.sharedAPI.getSkizzeByName(name);
        skizze = { base64, name };
      } catch (er) {
        if ((er as HttpErrorResponse).status == 404)
          this.toastr.warning(this.mrTranslate.transform('Keine Skizze am Server vorhanden'));
      }
    }
    preview.unpackFiles([skizze]);
  }

  /** neue Skizze hochgeladen oder aktuelle gelöscht */
  protected async setBildInForm(name: string) {
    if (!name) {
      this.nameGroupForm.controls.Bild.setValue(null, { emitEvent: false });
      this.nameGroupForm.markAsDirty();
      return;
    }
    name = name.slice(0, -4);

    const isExisting = this.skizzenListe().includes(name);
    const ok = () =>
      this.confirmService.warning(`
        ${this.mrTranslate.transform('Es ist bereits eine Skizze mit diesem Namen vorhanden')}
        ${this.mrTranslate.transform('Möchten Sie die Skizze ersetzen?')}
        `);
    if (isExisting && !(await ok())) return;

    try {
      const [file] = await this.preview_aktiv().packUnsavedFiles();
      await this.sharedAPI.saveSkizzeByName(name, file.base64);
      if (!isExisting) this.skizzenListe.update(names => (names.push(name), [...names]));
      this.nameGroupForm.controls.Bild.setValue(name, { emitEvent: false });
      this.nameGroupForm.markAsDirty();
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert!'));
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async startExport() {
    //* alle, wenn keine selectedIDs
    const selectedIDs = this.isMultiModeNames() ? this.selectedNames().map(name => name.ID) : [];
    const res = await this.api.exportZaehler(this.otypid(), this.mode(), selectedIDs);

    if (!res) {
      this.toastr.warning(this.mrTranslate.transform('Kein Export erstellt, Fehler im Log.'));
      return;
    }

    const blob = new Blob([res], { type: 'application/json' });
    const anchor = document.createElement('a');
    anchor.download = `Export ${this.objekte()[this.otypid()].bezeichnung}.json`;
    anchor.href = (window.webkitURL || window.URL).createObjectURL(blob);
    anchor.click();
  }

  protected close() {
    this.closedialog.next(true);
  }

  private checkDuplicateCodes() {
    const codes = this.zNamesCodes();
    const unique_codes = new Set(codes);

    if (unique_codes.size != codes.length)
      this.toastr.warning(
        this.mrTranslate.transform('Es wurde eine Zählerprogramme mit einem bereits verwendeten Code importiert.')
      );
  }

  protected async moveDrop(event: CdkDragDrop<ZählerWert[], ZählerWert[], number>) {
    if (event.previousContainer != event.container) return;
    const change = event.currentIndex - event.previousIndex;
    if (change == 0) return;

    try {
      const rows = [...this.werte()];
      const oldIndex = event.item.data;
      const newIndex = oldIndex + change;

      moveItemInArray(rows, oldIndex, newIndex);

      await this.api.resortZaehlerWerte(rows.map(r => r.ID));
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert'));
      this.zaehlerWerteRs.reload();
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }
}

function getTracks(name: ZählerName) {
  if (!name) return [];
  const tracks = [name.Track0];
  if (TrackMode[name.Track1]) {
    tracks.push(name.Track1);
    if (TrackMode[name.Track2]) {
      tracks.push(name.Track2);
      if (TrackMode[name.Track3]) {
        tracks.push(name.Track3);
      }
    }
  }

  return tracks;
}
